/* AdminLevelControls.js */

import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Snackbar,
  TextField,
  Collapse,
} from "@mui/material";

import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";

import { PermissionType, BuildSubmissionUrl, BuildReviewUrl } from "../../../Enums";
import TokenCarousel from "./TokenCarousel";
import ProjectActivityFeed from "./ProjectActivityFeed";
import TokenRow from "./TokenRow";
import AdminSpeedDial from "./AdminSpeedDial";
import SuperAdminSpeedDial from "./SuperAdminSpeedDial";
import { triggerDawnSummary } from "../../../helpers/EventHelpers";

// Super admin panel components
import UserFields from "../UserFields";
import AdminUsers from "./AdminUsers";
import CloneProject from "./CloneProject";
import HiddenSubmissionsDataGrid from "./HiddenSubmissionsDataGrid";
import ProjectHistoryDataGrid from "./ProjectHistory";
import AssetIndexDataGrid from "./AssetIndex";
import AdminJobsPanel from "./AdminJobsPanel";

import "./AdminLevelControls.css";

const formatAdminTokensToString = (tokensObj) => {
  if (!tokensObj || typeof tokensObj !== "object") return "";

  return Object.entries(tokensObj)
    .map(([account, tokens]) => {
      if (!Array.isArray(tokens)) return "";
      return `${account}:${tokens.join(",")};`;
    })
    .filter(Boolean)
    .join("");
};

const darkTooltipSx = {
  bgcolor: "#111",
  color: "#fff",
  fontSize: 12,
};

const PanelType = {
  // Admin panels
  ADMIN_TOKENS: "adminTokens",
  TOKEN_CAROUSEL: "tokenCarousel",
  ROLE: "role",
  PROJECT_NAME: "projectName",

  // Super admin panels
  ADD_ADMIN: "addAdmin",
  ADMIN_USERS: "adminUsers",
  HIDDEN_SUBMISSIONS: "hiddenSubmissions",
  CLONE_PROJECT: "cloneProject",
  PROJECT_HISTORY: "projectHistory",
  ASSET_INDEX: "assetIndex",
  ADMIN_JOBS: "adminJobs",
};

const AdminLevelControls = ({
  projectHistoryLogs,

  currentProjectName,
  currentProjectToken,

  submissionToken,
  reviewingTokens,
  activeReviewToken,
  setActiveReviewToken,

  submissionTokens,
  activeSubmissionToken,
  setActiveSubmissionToken,

  appUser,
  adminTokens,
  isAdminOpen,
  onProjectsChange,

  customTheme,

  // Super admin props
  allSubmissions,
  treeData,
  user,
  adminUsers,
  currentProject,
  currentForm,
  assets,
  processedFields,
  viewfieldsInstance,
  handleTokenClick,
  handleReNumberClick,
  onNewProject,
  onCellUpdate,
  onCreateUser,
}) => {
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const [projectNameDraft, setProjectNameDraft] = useState(currentProjectName || "");
  const [adminTokenStringDraft, setAdminTokenStringDraft] = useState(
    formatAdminTokensToString(adminTokens)
  );
  const [activePanel, setActivePanel] = useState(null);
  const isWidePanel =
    activePanel === PanelType.ADMIN_USERS ||
    activePanel === PanelType.HIDDEN_SUBMISSIONS ||
    activePanel === PanelType.PROJECT_HISTORY ||
    activePanel === PanelType.ASSET_INDEX;

  const isSuperAdmin = appUser?.adminLevel === PermissionType.SUPER_ADMIN;

  useEffect(() => {
    setProjectNameDraft(currentProjectName || "");
  }, [currentProjectName]);

  useEffect(() => {
    setAdminTokenStringDraft(formatAdminTokensToString(adminTokens));
  }, [adminTokens]);

  useEffect(() => {
    if (activePanel === PanelType.PROJECT_NAME) {
      setProjectNameDraft(currentProjectName || "");
    }
  }, [activePanel, currentProjectName]);

  // Submission tokens - current state: single string only
  const submissionTokensList = useMemo(() => {
    if (typeof submissionToken === "string" && submissionToken.trim()) {
      return [submissionToken.trim()];
    }
    return [];
  }, [submissionToken]);

  // Review tokens - already an array
  const reviewTokensList = useMemo(() => {
    if (!Array.isArray(reviewingTokens)) return [];
    return reviewingTokens.filter(Boolean).sort((a, b) => a.localeCompare(b));
  }, [reviewingTokens]);

  const hasMultipleSubmissionTokens = submissionTokensList.length > 1;
  const hasMultipleReviewTokens = reviewTokensList.length > 1;

  // Active tokens
  const activeSubmissionTokenEffective = submissionTokensList.length
    ? activeSubmissionToken || submissionTokensList[0]
    : "";

  const activeReviewTokenEffective = reviewTokensList.length
    ? activeReviewToken || reviewTokensList[0]
    : "";

  const themePrimary = customTheme.mainColour;
  const themeSecondary = customTheme.headerWidgetColour;
  const themeSubmission = customTheme.submissionColour;
  const themeReview = customTheme.reviewColour;

  const showSnackbarMessage = (message) => {
    setSnackbarMessage(message);
    setIsSnackbarOpen(true);
  };

  const copyToClipboard = async (value, successMessage) => {
    try {
      await navigator.clipboard.writeText(value);
      showSnackbarMessage(successMessage);
    } catch (error) {
      console.error("Clipboard failed:", error);
      showSnackbarMessage("Copy failed");
    }
  };

  const openUrlInNewTab = (url) => {
    const newWindow = window.open(url, "_blank", "noopener,noreferrer");
    if (!newWindow) {
      showSnackbarMessage("Failed to open URL - popup may be blocked");
    }
  };

  const togglePanel = (panelType) => {
    setActivePanel((prev) => (prev === panelType ? null : panelType));
  };

  const closeAllPanels = () => {
    setActivePanel(null);
  };

  const saveProjectNameDraft = () => {
    const trimmedName = projectNameDraft.trim();

    if (!trimmedName) {
      showSnackbarMessage("Project name cannot be empty");
      return false;
    }

    onProjectsChange(currentProjectToken, { id: currentProjectToken, name: trimmedName });
    showSnackbarMessage("Project name updated");
    return true;
  };

  const handleSelectSubmissionToken = (token) => {
    if (!hasMultipleSubmissionTokens) return;
    setActiveSubmissionToken(token);
  };

  const handleSelectReviewToken = (token) => {
    if (!hasMultipleReviewTokens) return;
    setActiveReviewToken(token);
  };

  const handleMakeDefaultToken = (tokenIndex) => {
    console.log("Make default for token at index:", tokenIndex);
  };

  const handleTriggerDawn = async () => {
    try {
      console.log("🌅 DAWN TRIGGER: Starting...");

      const userId = appUser?.id;

      console.log("🌅 DAWN TRIGGER: User Info:", {
        userId,
        projectToken: currentProjectToken,
        submissionToken,
        reviewingTokens,
      });

      if (!userId) {
        console.error("❌ DAWN TRIGGER: Missing userId");
        showSnackbarMessage("Unable to trigger Dawn: User not found");
        return;
      }

      if (!currentProjectToken) {
        console.error("❌ DAWN TRIGGER: Missing projectToken");
        showSnackbarMessage("Unable to trigger Dawn: Project not found");
        return;
      }

      if (!submissionToken) {
        console.error("❌ DAWN TRIGGER: Missing submissionToken");
        showSnackbarMessage("Unable to trigger Dawn: Submission token not found");
        return;
      }

      const reviewToken =
        Array.isArray(reviewingTokens) && reviewingTokens.length > 0 ? reviewingTokens[0] : null;

      if (!reviewToken) {
        console.error("❌ DAWN TRIGGER: Missing reviewToken");
        showSnackbarMessage("Unable to trigger Dawn: Review token not found");
        return;
      }

      console.log("🌅 DAWN TRIGGER: Calling triggerDawnSummary...");

      const success = await triggerDawnSummary({
        userId,
        projectToken: currentProjectToken,
        submissionFormToken: submissionToken,
        reviewFormToken: reviewToken,
        userEmail: appUser?.email,
        userGivenName: appUser?.givenName,
        userFamilyName: appUser?.familyName,
        manual: true,
      });

      if (success) {
        console.log("✅ DAWN TRIGGER: Event raised successfully");
        showSnackbarMessage("✅ Dawn summary triggered! Check console in 30-60 seconds.");
      } else {
        console.error("❌ DAWN TRIGGER: Event failed to raise");
        showSnackbarMessage("❌ Failed to trigger Dawn summary. Check console.");
      }
    } catch (err) {
      console.error("❌ DAWN TRIGGER: Exception:", err);
      showSnackbarMessage("❌ Error triggering Dawn: " + err.message);
    }
  };

  const getUserRoleLabel = () => {
    return isSuperAdmin ? "Super admin" : "Admin";
  };

  const activeSubmissionUrl = activeSubmissionTokenEffective
    ? BuildSubmissionUrl(activeSubmissionTokenEffective)
    : "";

  const activeReviewUrl = activeReviewTokenEffective ? BuildReviewUrl(activeReviewTokenEffective) : "";

  if (!isAdminOpen) return null;

  return (
    <Box
      className={`alcRoot ${isWidePanel ? "alcRoot--wide" : ""}`}
      style={{
        ["--alc-primary"]: themePrimary,
        ["--alc-secondary"]: themeSecondary,
        ["--alc-submission"]: themeSubmission,
        ["--alc-review"]: themeReview,
      }}
    >
      <div className="alcHeaderRow">
        {isSuperAdmin && (
          <SuperAdminSpeedDial
            activePanel={activePanel}
            onTogglePanel={togglePanel}
            onCloseAllPanels={closeAllPanels}
            onAddToken={handleTokenClick}
            onReNumber={handleReNumberClick}
            onTriggerDawn={handleTriggerDawn}
          />
        )}

        <div className="project-card-header">{currentProjectName || "Untitled project"}</div>

        <AdminSpeedDial
          activePanel={activePanel}
          onTogglePanel={togglePanel}
          onCloseAllPanels={closeAllPanels}
          onCopyProjectToken={() => copyToClipboard(currentProjectToken || "", "Project token copied")}
          onOpenSubmissionUrl={() => {
            if (!activeSubmissionUrl) return showSnackbarMessage("No active submission token");
            openUrlInNewTab(activeSubmissionUrl);
          }}
          onOpenReviewUrl={() => {
            if (!activeReviewUrl) return showSnackbarMessage("No active review token");
            openUrlInNewTab(activeReviewUrl);
          }}
          onCopySubmissionUrl={() => {
            if (!activeSubmissionUrl) return showSnackbarMessage("No active submission token");
            copyToClipboard(activeSubmissionUrl, "Submission URL copied");
          }}
          onCopyReviewUrl={() => {
            if (!activeReviewUrl) return showSnackbarMessage("No active review token");
            copyToClipboard(activeReviewUrl, "Review URL copied");
          }}
        />
      </div>

      {/* Admin Panels */}
      <Collapse in={activePanel === PanelType.ADMIN_TOKENS} timeout={180} unmountOnExit>
        <div
          className={`alcInlineAdminPanel ${
            activePanel === PanelType.ADMIN_TOKENS ? "alcInlineAdminPanel--active" : ""
          }`}
        >
          <Typography className="alcAdminDialogLabel" variant="subtitle2">
            Admin tokens (string)
          </Typography>

          <textarea
            className="alcAdminTextarea"
            value={adminTokenStringDraft}
            onChange={(e) => setAdminTokenStringDraft(e.target.value)}
            placeholder="Enter token string"
          />
        </div>
      </Collapse>

      <Collapse in={activePanel === PanelType.TOKEN_CAROUSEL} timeout={180} unmountOnExit>
        <div
          className={`alcInlineAdminPanel ${
            activePanel === PanelType.TOKEN_CAROUSEL ? "alcInlineAdminPanel--active" : ""
          }`}
        >
          <Typography className="alcAdminDialogLabel" variant="subtitle2">
            Token selector
          </Typography>

          <div className="alcCarouselWrap">
            <TokenCarousel adminTokens={adminTokens} onMakeDefault={handleMakeDefaultToken} />
          </div>
        </div>
      </Collapse>

      <Collapse in={activePanel === PanelType.ROLE} timeout={180} unmountOnExit>
        <div
          className={`alcInlineAdminPanel ${
            activePanel === PanelType.ROLE ? "alcInlineAdminPanel--active" : ""
          }`}
        >
          <Typography className="alcAdminDialogLabel" variant="subtitle2">
            My role
          </Typography>
          <div className="alcInlineSingleLine">{getUserRoleLabel()}</div>
        </div>
      </Collapse>

      <Collapse in={activePanel === PanelType.PROJECT_NAME} timeout={180} unmountOnExit>
        <div
          className={`alcInlineAdminPanel ${
            activePanel === PanelType.PROJECT_NAME ? "alcInlineAdminPanel--active" : ""
          }`}
        >
          <Typography className="alcAdminDialogLabel" variant="subtitle2">
            Project name
          </Typography>

          <div className="alcEditNameRow">
            <TextField
              value={projectNameDraft}
              onChange={(e) => setProjectNameDraft(e.target.value)}
              fullWidth
              multiline
              minRows={3}
              maxRows={10}
              autoFocus
            />

            <div className="alcEditNameActions">
              <Tooltip title="Save" placement="top" componentsProps={{ tooltip: { sx: darkTooltipSx } }}>
                <IconButton
                  onClick={() => {
                    const ok = saveProjectNameDraft();
                    if (ok) setActivePanel(null);
                  }}
                >
                  <CheckIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title="Cancel" placement="bottom" componentsProps={{ tooltip: { sx: darkTooltipSx } }}>
                <IconButton onClick={() => setActivePanel(null)}>
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </div>
          </div>
        </div>
      </Collapse>

      {/* Super Admin Panels */}
      {isSuperAdmin && (
        <>
          <Collapse in={activePanel === PanelType.ADD_ADMIN} timeout={180} unmountOnExit>
            <div className="alcInlineAdminPanel alcInlineAdminPanel--active">
              <UserFields
                title="Add admin user."
                onClose={() => togglePanel(PanelType.ADD_ADMIN)}
                onCreateUser={(userData) => {
                  const modifiedUserData = {
                    ...userData,
                    projectAdmin: true,
                  };
                  onCreateUser(modifiedUserData);

                  // Optional: auto-close after successful create
                  togglePanel(PanelType.ADD_ADMIN);
                }}
              />
            </div>
          </Collapse>

          <Collapse in={activePanel === PanelType.ADMIN_USERS} timeout={180} unmountOnExit>
            <div className="alcInlineAdminPanel alcInlineAdminPanel--active alcInlineAdminPanel--wide">
              <AdminUsers
                adminUsers={adminUsers}
                onClose={() => togglePanel(PanelType.ADMIN_USERS)}
                projectName={currentProjectName}
                projectToken={currentProjectToken}
              />
            </div>
          </Collapse>

          <Collapse in={activePanel === PanelType.HIDDEN_SUBMISSIONS} timeout={180} unmountOnExit>
            <div className="alcInlineAdminPanel alcInlineAdminPanel--active alcInlineAdminPanel--wide">
              <HiddenSubmissionsDataGrid
                allSubmissions={allSubmissions}
                onCellUpdate={onCellUpdate}
                submissionToken={submissionToken}
                reviewingTokens={reviewingTokens}
                onClose={() => togglePanel(PanelType.HIDDEN_SUBMISSIONS)}
              />
            </div>
          </Collapse>

          <Collapse in={activePanel === PanelType.CLONE_PROJECT} timeout={180} unmountOnExit>
            <div className="alcInlineAdminPanel alcInlineAdminPanel--active">
              <CloneProject
                user={user}
                treeData={treeData}
                onClose={() => togglePanel(PanelType.CLONE_PROJECT)}
                onNewProject={onNewProject}
              />
            </div>
          </Collapse>

          <Collapse in={activePanel === PanelType.PROJECT_HISTORY} timeout={180} unmountOnExit>
            <div className="alcInlineAdminPanel alcInlineAdminPanel--active alcInlineAdminPanel--wide">
              <ProjectHistoryDataGrid
                logs={projectHistoryLogs}
                onClose={() => togglePanel(PanelType.PROJECT_HISTORY)}
              />
            </div>
          </Collapse>

          <Collapse in={activePanel === PanelType.ASSET_INDEX} timeout={180} unmountOnExit>
            <div className="alcInlineAdminPanel alcInlineAdminPanel--active alcInlineAdminPanel--wide">
              <AssetIndexDataGrid onClose={() => togglePanel(PanelType.ASSET_INDEX)} />
            </div>
          </Collapse>

          <Collapse in={activePanel === PanelType.ADMIN_JOBS} timeout={180} unmountOnExit>
            <div className="alcInlineAdminPanel alcInlineAdminPanel--active">
              <AdminJobsPanel
                allSubmissions={allSubmissions}
                currentProject={currentProject}
                currentForm={currentForm}
                assets={assets}
                fields={processedFields}
                viewfieldsInstance={viewfieldsInstance}
                onClose={() => togglePanel(PanelType.ADMIN_JOBS)}
              />
            </div>
          </Collapse>
        </>
      )}

      {/* Token Display Grid */}
      <div className="alcTokensFieldset" role="group" aria-label="Tokens">
        <div className="alcTokensLegend">Tokens</div>

        <div className="alcTokensGrid">
          <div className="alcTokenCol">
            <div className="alcTokenColTitle">Submission</div>

            <div className="alcTokenColRows">
              {hasMultipleSubmissionTokens ? (
                submissionTokensList.map((token, idx) => {
                  const isSelected = token === activeSubmissionTokenEffective;
                  const url = BuildSubmissionUrl(token);

                  return (
                    <TokenRow
                      key={`${token || "UNKNOWN"}-${idx}`}
                      colorVariant="submission"
                      token={token}
                      url={url}
                      tokenType="Submission"
                      isSelected={isSelected}
                      isSelectable={true}
                      onSelect={() => handleSelectSubmissionToken(token)}
                      onCopyToken={copyToClipboard}
                      onOpenUrl={openUrlInNewTab}
                      darkTooltipSx={darkTooltipSx}
                    />
                  );
                })
              ) : activeSubmissionTokenEffective ? (
                <TokenRow
                  colorVariant="submission"
                  token={activeSubmissionTokenEffective}
                  url={BuildSubmissionUrl(activeSubmissionTokenEffective)}
                  tokenType="Submission"
                  isSelected={false}
                  isSelectable={false}
                  onSelect={() => {}}
                  onCopyToken={copyToClipboard}
                  onOpenUrl={openUrlInNewTab}
                  darkTooltipSx={darkTooltipSx}
                />
              ) : (
                <TokenRow
                  colorVariant="submission"
                  token=""
                  url=""
                  tokenType="Submission"
                  isSelected={false}
                  isSelectable={false}
                  onCopyToken={copyToClipboard}
                  onOpenUrl={openUrlInNewTab}
                  darkTooltipSx={darkTooltipSx}
                />
              )}
            </div>
          </div>

          <div className="alcTokenCol">
            <div className="alcTokenColTitle">Review</div>

            <div className="alcTokenColRows">
              {hasMultipleReviewTokens ? (
                reviewTokensList.map((token, idx) => {
                  const isSelected = token === activeReviewTokenEffective;
                  const url = BuildReviewUrl(token);

                  return (
                    <TokenRow
                      key={`${token || "UNKNOWN"}-${idx}`}
                      colorVariant="review"
                      token={token}
                      url={url}
                      tokenType="Review"
                      isSelected={isSelected}
                      isSelectable={true}
                      onSelect={() => handleSelectReviewToken(token)}
                      onCopyToken={copyToClipboard}
                      onOpenUrl={openUrlInNewTab}
                      darkTooltipSx={darkTooltipSx}
                    />
                  );
                })
              ) : activeReviewTokenEffective ? (
                <TokenRow
                  colorVariant="review"
                  token={activeReviewTokenEffective}
                  url={BuildReviewUrl(activeReviewTokenEffective)}
                  tokenType="Review"
                  isSelected={false}
                  isSelectable={false}
                  onSelect={() => {}}
                  onCopyToken={copyToClipboard}
                  onOpenUrl={openUrlInNewTab}
                  darkTooltipSx={darkTooltipSx}
                />
              ) : (
                <TokenRow
                  colorVariant="review"
                  token=""
                  url=""
                  tokenType="Review"
                  isSelected={false}
                  isSelectable={false}
                  onCopyToken={copyToClipboard}
                  onOpenUrl={openUrlInNewTab}
                  darkTooltipSx={darkTooltipSx}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="alcLogPanel">
        <div className="alcLogInlineTitle">Project History</div>
        <ProjectActivityFeed logs={projectHistoryLogs} />
      </div>

      <Snackbar
        open={isSnackbarOpen}
        autoHideDuration={2200}
        onClose={() => setIsSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default AdminLevelControls;
